import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Organization } from "@dreamhub/designsystem/icons";
import DreamerFieldCard from "@/components/controls/DreamerFieldCard";
import DreamerMatchList, {
  DreamerMatch
} from "@/components/controls/DreamerMatchList";
import { DuplicateMatch, useCheckCompanyDuplicates } from "../hooks";
import { isPersonalEmailDomain, isValidWebsite } from "@/utils/domains";

// Long enough that "a"/"an" don't each fire a request matching half the tenant.
const MIN_LOOKUP_LENGTH = 3;

// Shorter than the manual tab's 500ms — the warning is only useful mid-typing. Exported so tests can't drift.
export const LOOKUP_DEBOUNCE_MS = 250;

interface Props {
  website: string;
  setWebsite: (website: string) => void;
  /** Set by the parent once a submit has been attempted, so the field isn't red while typing. */
  showValidation: boolean;
  onClose: () => void;
}

/** "With Dreamer" tab: one website field Dreamer enriches into a full company; matches shown are advisory only. */
const CreateCompanyWithDreamer: React.FC<Props> = ({
  website,
  setWebsite,
  showValidation,
  onClose
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [debouncedWebsite, setDebouncedWebsite] = useState("");
  const [dismissedSimilar, setDismissedSimilar] = useState(false);

  const trimmedWebsite = website.trim();
  const isInvalid =
    showValidation && trimmedWebsite.length > 0 && !isValidWebsite(trimmedWebsite);

  useEffect(() => {
    if (!trimmedWebsite) {
      setDebouncedWebsite("");
      return;
    }
    const timer = setTimeout(
      () => setDebouncedWebsite(trimmedWebsite),
      LOOKUP_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [trimmedWebsite]);

  // Reset the dismissal whenever the domain changes — a new domain is a new warning.
  useEffect(() => {
    setDismissedSimilar(false);
  }, [website]);

  // Not gated on isValidWebsite: partials like "anthro" should surface clashes mid-typing via the backend's contains-match.
  const isSearchableFragment =
    debouncedWebsite.length >= MIN_LOOKUP_LENGTH && !/\s/.test(debouncedWebsite);

  const duplicateCheck = useCheckCompanyDuplicates(
    debouncedWebsite,
    isSearchableFragment
  );

  // Dreamer never blocks on duplicates — exact and similar go in one advisory list.
  const matches = useMemo<DreamerMatch[]>(() => {
    const duplicateCheckData = duplicateCheck.data;
    if (!duplicateCheckData) return [];
    const combined: DuplicateMatch[] = [
      ...(duplicateCheckData.exactMatches ?? []),
      ...(duplicateCheckData.similarMatches ?? [])
    ];
    return combined.map((match) => ({
      id: match.companyId,
      title: match.companyName,
      subtitle: match.domain
    }));
  }, [duplicateCheck.data]);

  // Not gated on isFetching — blanking the box on every keystroke's refetch made it flicker.
  const showMatches = !dismissedSimilar && matches.length > 0;

  const errorMessage = isInvalid
    ? isPersonalEmailDomain(trimmedWebsite)
      ? t("companies.createModal.withDreamer.personalDomain")
      : t("companies.createModal.withDreamer.invalidUrl")
    : null;

  return (
    <DreamerFieldCard
      hint={t("companies.createModal.withDreamer.hint")}
      label={t("companies.createModal.withDreamer.websiteLabel")}
      placeholder={t("companies.createModal.withDreamer.websitePlaceholder")}
      value={website}
      onChange={setWebsite}
      error={errorMessage}
      dataTest="create-company-dreamer-website"
    >
      {showMatches ? (
        <DreamerMatchList
          title={
            matches.length === 1
              ? t("companies.createModal.duplicateSimilarSingle")
              : t("companies.createModal.duplicateSimilarMultiple", {
                  count: matches.length
                })
          }
          matches={matches}
          openLabel={t("companies.createModal.open")}
          dismissLabel={t("dismiss")}
          matchIcon={<Organization />}
          onDismiss={() => setDismissedSimilar(true)}
          onOpen={(companyId) => {
            onClose();
            navigate(`/companies/${companyId}`);
          }}
          dataTest="create-company-dreamer-similar-domains"
        />
      ) : null}
    </DreamerFieldCard>
  );
};

export default CreateCompanyWithDreamer;
